import { Col, Row } from 'antd';
import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import { GetServerSideProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { ReactElement } from 'react';
import styled from 'styled-components';

import CuripodNormal from '../../assets/icons/CuripodNormal';
import LongArrowRightSvg from '../../assets/icons/LongArrowRightSvg';
import emptyProfilePhoto from '../../assets/images/emptyProfilePhoto.png';
import theme from '../../assets/theme';
import { IconWrapper } from '../../components/_atoms/IconWrapper';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import { NextPageWithLayout } from '../../components/layouts';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { ProfileCircle } from '../../components/ProfileCircle';
import { Creator } from '../../services/CreatorService/Creator.type';
import CreatorService from '../../services/CreatorService/CreatorService';
import { getDiscoverCreatorUrl } from '../../services/DiscoverService/discoverUtils';
import { configureStore } from '../../utils/store';
const store = configureStore();

const cdnUrl = store.config.cdn.url;

const CardContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  background: ${theme.colors.white};
  height: 100%;
  width: 100%;
  min-width: 220px;
  min-height: 170px;
  border-radius: 34px;
  position: relative;
  overflow: hidden;
  box-shadow: 0px 4px 4px rgb(0, 0, 0, 0.1);
  padding: 40px 60px 30px 60px;

  h2 {
    font-weight: 750;
    font-size: 48px;
    color: ${theme.colors.black};
    margin-bottom: 0;
  }
  p {
    font-weight: bold;
    font-size: 26px;
    color: ${theme.colors.black};
    margin-bottom: 0;
  }

  @media (max-width: 600px) {
    padding: 20px 30px;
    h2 {
      font-size: 30px;
    }

    p {
      font-size: 20px;
    }
  }
`;

const CardRow = styled.div`
  width: 100%;
`;

const HorisontalDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e4e4e4;
`;

const CreatorCard = ({ creator }: { creator: Creator }) => {
  const animation = useAnimation();

  const hoverSequence = async () => {
    await animation.start({ scale: 1.03, transition: { duration: 0.1 } });
    animation.start({ scale: 1, transition: { duration: 0.1 } });
  };

  const authorLink = getDiscoverCreatorUrl(creator.nickName, creator.id);

  return (
    <AnimatePresence>
      <Link href={authorLink} passHref>
        <a>
          <CardContainer animate={animation} onHoverStart={hoverSequence}>
            <div>
              <CardRow>
                {creator.thumbnailId ? (
                  <ProfileCircle $radius={80}>
                    <Image
                      style={{ borderRadius: '50%' }}
                      width={'100%'}
                      height={'100%'}
                      src={
                        creator.thumbnailId
                          ? `${cdnUrl}/${creator.thumbnailId}.jpeg`
                          : emptyProfilePhoto
                      }
                      alt="profile"
                    />
                  </ProfileCircle>
                ) : (
                  <div style={{ width: 'fit-content' }}>
                    <IconWrapper height="80px" width="80px" color={theme.colors.orange}>
                      <CuripodNormal />
                    </IconWrapper>
                  </div>
                )}
              </CardRow>
              <CardRow>
                <HorisontalDivider style={{ margin: '30px 0px' }} />
              </CardRow>
              <CardRow style={{ marginBottom: 20 }}>
                <h2>{creator.nickName}</h2>
              </CardRow>
              <CardRow>
                <p>{creator.description}</p>
              </CardRow>
            </div>
            <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
              <IconWrapper height="36px" width="36px" color={theme.colors.black}>
                <LongArrowRightSvg />
              </IconWrapper>
            </div>
          </CardContainer>
        </a>
      </Link>
    </AnimatePresence>
  );
};

const CreatorsPage: NextPageWithLayout<{ creators: Creator[] }> = ({ creators }) => {
  return (
    <MaxWidthContainer style={{ padding: '40px 0px' }}>
      <StatementTitle>
        Curipod creators who share their lessons, presentations, workshops and other
        creations with the rest of the Curipod community deserve a place in the spot
        light. This page gives you an overview of some of the amazing Curipod creators.
      </StatementTitle>
      <Row gutter={[10, 20]} style={{ marginTop: 40 }}>
        {creators.map(c => (
          <Col
            key={c.id}
            xs={24}
            sm={24}
            md={24}
            lg={12}
            xl={12}
            xxl={12}
            style={{ minHeight: 600 }}
          >
            <CreatorCard creator={c} />
          </Col>
        ))}
      </Row>
    </MaxWidthContainer>
  );
};

const StatementTitle = styled.h3`
  text-align: center;
  font-weight: 500;
  color: ${theme.colors.black};
  padding-top: 45px;
  padding-bottom: 45px;
  font-size: 28px;
  max-width: 700px;
  margin: auto;
  @media ${theme.device.tablet} {
    font-size: 28px;
  }
  @media ${theme.device.mobileL} {
    font-size: 22px;
  }
`;

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );
  const creators = await CreatorService.listCreators();

  const creatorsWithPublishedContent = creators
    .filter(c => c.curipodsCount > 0)
    .sort((a, b) => b.curipodsCount - a.curipodsCount);

  return {
    props: {
      creators: creatorsWithPublishedContent,
    },
  };
};

CreatorsPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <LandingPageLayout
      title="Curipod creators"
      subTitle="Here are some of the most popular creators in the Curipod community"
      primaryActionText="Sign up - It's free"
      hideBlobs={false}
    >
      {page}
    </LandingPageLayout>
  );
};

export default CreatorsPage;
